/**
 * Duplicate-account warning interstitial (AUTH-154).
 *
 * Reached from the index decision tree when the signed-in account is flagged
 * as a duplicate of a canonical account (DB-108 / ABC-1265). Encourages the
 * user to sign in with the canonical email instead, but lets them proceed as
 * the current (duplicate) account if they choose.
 */
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Button } from "@better-bookkeeping/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { AlertTriangle } from "lucide-react";
import { getPendingInvitation, withInviteTokenSearch } from "@/lib/pending-pricing-invite";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { getFallbackRedirectUrl } from "@/lib/redirect-utils";

export const Route = createFileRoute("/duplicate-account")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
    email: z.string().optional(),
    duplicateOfEmail: z.string().optional(),
  }).parse,
  component: DuplicateAccountPage,
});

export function DuplicateAccountPage() {
  const { redirect_uri, email, duplicateOfEmail } = Route.useSearch();
  const runtimeConfig = useRuntimeConfig();
  const navigate = useNavigate();

  // This route can leave the gateway, so an invitation awaiting explicit
  // account confirmation always returns to the confirmation boundary first.
  if (getPendingInvitation()) {
    return <Navigate to="/" search={{ redirect_uri }} />;
  }

  // Nothing to warn about without a canonical account — send the user on.
  if (!duplicateOfEmail) {
    return <Navigate to="/" search={{ redirect_uri }} />;
  }

  const proceed = () => {
    window.location.href = redirect_uri ?? getFallbackRedirectUrl(runtimeConfig);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1 text-center">
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="duplicate-account-title">
          This account is a duplicate
        </h2>
      </div>

      <Alert variant="warning" data-testid="duplicate-account-warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Use your main account</AlertTitle>
        <AlertDescription>
          The login {email ?? "you used"} has been flagged as a duplicate of {duplicateOfEmail}. To
          see the most comprehensive and up-to-date data, please log in with {duplicateOfEmail}.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-2 pt-1">
        <Button
          className="w-full"
          onClick={() => navigate({ to: "/login", search: { redirect_uri } })}
          data-testid="duplicate-account-back-to-login-button"
        >
          Back to login
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={proceed}
          data-testid="duplicate-account-proceed-button"
        >
          Proceed anyway
        </Button>
      </div>
    </div>
  );
}
